import { useEffect, useState } from "react";
import { AdminInbox } from "@/lib/api";
import { AdminLayout } from "./AdminPages";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast, confirmDialog } from "@/components/ui/dialog";
import { Send } from "lucide-react";
import { useRoute } from "@/lib/router";

type Scope = "user" | "role" | "all";

export function AdminInboxSendPage() {
  const route = useRoute();
  const [scope, setScope] = useState<Scope>("user");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [kind, setKind] = useState("broadcast");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [alsoEmail, setAlsoEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  // 从用户管理页带 ?email=xxx 跳转过来时自动填充
  useEffect(() => {
    if (route.query.email) { setEmail(route.query.email); setScope("user"); }
  }, [route.query.email]);

  const submit = async () => {
    if (!title.trim()) { toast.warning("标题必填"); return; }
    if (scope === "user" && !email.trim()) { toast.warning("请提供收件人邮箱"); return; }
    if (scope === "all" && !await confirmDialog({
      title: "确认全站广播？",
      description: "将向全站所有用户发送该站内信，不可撤回。",
      destructive: true, confirmText: "发送给所有人",
    })) return;
    if (scope === "role" && !await confirmDialog({
      title: `确认向角色「${role}」发送？`,
      description: "该角色下所有用户都会收到这条站内信。",
      confirmText: "发送",
    })) return;

    setBusy(true);
    try {
      const r = await AdminInbox.send({
        scope, kind: kind || "broadcast", title, body, also_email: alsoEmail,
        ...(scope === "user" ? { email: email.trim() } : {}),
        ...(scope === "role" ? { role } : {}),
      });
      toast.success(`已发送：${r.recipients} 位收件人，写入 ${r.sent} 条`);
      setTitle(""); setBody("");
    } catch (e: any) {
      toast.error(e?.body?.message || e?.message || "发送失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <Card>
        <CardHeader><CardTitle>发送站内信</CardTitle></CardHeader>
        <CardContent className="space-y-4 max-w-2xl">
          <div>
            <Label>收件范围</Label>
            <div className="flex gap-2 mt-1.5">
              {[
                { v: "user", t: "指定用户" },
                { v: "role", t: "按角色" },
                { v: "all",  t: "全站广播" },
              ].map(o => (
                <Button key={o.v} type="button" size="sm"
                        variant={scope === o.v ? "default" : "outline"}
                        onClick={() => setScope(o.v as Scope)}>{o.t}</Button>
              ))}
            </div>
          </div>

          {scope === "user" && (
            <div>
              <Label>收件人邮箱</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="user@example.com" type="email" autoComplete="off" />
              <p className="text-[11px] text-muted-foreground mt-1">
                提示：可在「用户管理」中点击对应用户右侧的「发站内信」按钮一键打开本页并自动填入邮箱。
              </p>
            </div>
          )}
          {scope === "role" && (
            <div>
              <Label>角色 key</Label>
              <select className="h-9 w-full rounded-md border bg-background px-3"
                      value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="member">member（普通用户）</option>
                <option value="vip">vip（VIP 用户）</option>
                <option value="editor">editor（编辑）</option>
                <option value="admin">admin（管理员）</option>
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>消息分类（kind）</Label>
              <select className="h-9 w-full rounded-md border bg-background px-3"
                      value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="broadcast">系统公告</option>
                <option value="custom">自定义提醒</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={alsoEmail}
                       onChange={(e) => setAlsoEmail(e.target.checked)} />
                同步发送邮件副本
              </label>
            </div>
          </div>

          <div>
            <Label>标题</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="例如：系统升级公告" />
          </div>
          <div>
            <Label>正文</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)}
                      rows={8} placeholder="支持普通文本，邮件副本会做基础 HTML 转义" />
          </div>

          <div className="flex justify-end">
            <Button onClick={submit} disabled={busy}>
              <Send className="h-4 w-4 mr-1" /> {busy ? "发送中..." : "发送"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
